import { toCurrentRoutePath } from '@/config/legacyRoutePrefixes';

/**
 * Best-effort document context for the AI agent from the current URL (no page-specific hooks required).
 */
export type AiSelectionContext = {
  entityType: string;
  ids: string[];
  labels?: string[];
  listFilters?: Record<string, unknown>;
};

export interface AiPageContext {
  module?: string;
  docType?: string;
  docId?: string;
  filters?: Record<string, unknown>;
  /** Selected list rows / filters for contextual UDQ / assistant queries. */
  selection?: AiSelectionContext;
  /** Current pathname (no query) — lets the assistant pick the how-to guide for this screen. */
  route?: string;
  screen?: AiScreenKind;
}

export type AiScreenKind = 'list' | 'create' | 'detail' | 'edit' | 'other';

const U = '([0-9a-fA-F-]{36})';

export function screenKindFromPath(path: string): AiScreenKind {
  if (/\/new$/.test(path)) return 'create';
  if (/\/edit(\/[^/]+)?$/.test(path)) return 'edit';
  if (/\/[0-9a-fA-F-]{36}(\/[a-z-]+)?$/.test(path)) return 'detail';
  if (path.split('/').filter(Boolean).length >= 2) return 'list';
  return 'other';
}

export function getAiPageContextFromPath(pathname: string): AiPageContext {
  const p = toCurrentRoutePath((pathname || '').split('?')[0] || '');
  return { ...docContextFromPath(p), route: p || undefined, screen: p ? screenKindFromPath(p) : undefined };
}

function docContextFromPath(p: string): AiPageContext {

  const rules: { re: RegExp; ctx: Omit<AiPageContext, 'docId'> }[] = [
    { re: new RegExp(`^/growth/orders/${U}$`), ctx: { module: 'sales', docType: 'SalesOrder' } },
    { re: new RegExp(`^/growth/customers/${U}/360$`), ctx: { module: 'sales', docType: 'Customer' } },
    { re: new RegExp(`^/scm/procurement/suppliers/${U}$`), ctx: { module: 'procurement', docType: 'Supplier' } },
    { re: new RegExp(`^/asset/assets/${U}$`), ctx: { module: 'assets', docType: 'Asset' } },
    { re: new RegExp(`^/asset/assets/${U}/edit$`), ctx: { module: 'assets', docType: 'Asset' } },
    { re: new RegExp(`^/growth/contract-reviews/${U}$`), ctx: { module: 'contracts', docType: 'ContractReview' } },
    { re: new RegExp(`^/mfg/work-orders/${U}$`), ctx: { module: 'manufacturing', docType: 'WorkOrder' } },
    { re: new RegExp(`^/mfg/production-requests/${U}$`), ctx: { module: 'manufacturing', docType: 'ProductionRequest' } },
    { re: new RegExp(`^/prj/projects/${U}/edit$`), ctx: { module: 'projects', docType: 'Project' } },
    { re: new RegExp(`^/hr/employees/${U}$`), ctx: { module: 'hr', docType: 'Employee' } },
    { re: new RegExp(`^/qms/ncrs/${U}$`), ctx: { module: 'quality', docType: 'NCR' } },
    { re: new RegExp(`^/finance/payment-vouchers/${U}$`), ctx: { module: 'finance', docType: 'PaymentVoucher' } },
    { re: new RegExp(`^/crm/opportunities/${U}$`), ctx: { module: 'sales', docType: 'Opportunity' } },
    { re: new RegExp(`^/crm/quotations/${U}$`), ctx: { module: 'sales', docType: 'Quotation' } },
    { re: new RegExp(`^/scm/procurement/purchase-orders/${U}$`), ctx: { module: 'procurement', docType: 'PurchaseOrder' } },
    { re: new RegExp(`^/growth/delivery-notes/${U}$`), ctx: { module: 'sales', docType: 'DeliveryNote' } },
    { re: new RegExp(`^/growth/invoices/${U}$`), ctx: { module: 'sales', docType: 'SalesInvoice' } },
    { re: new RegExp(`^/growth/quotations/${U}$`), ctx: { module: 'sales', docType: 'Quotation' } },
    { re: new RegExp(`^/crm/quotations/${U}$`), ctx: { module: 'sales', docType: 'Quotation' } },
    { re: new RegExp(`^/growth/opportunities/${U}$`), ctx: { module: 'sales', docType: 'Opportunity' } },
    { re: new RegExp(`^/crm/opportunities/${U}$`), ctx: { module: 'sales', docType: 'Opportunity' } },
    { re: new RegExp(`^/scm/procurement/grn/${U}$`), ctx: { module: 'procurement', docType: 'GoodsReceiptNote' } },
    { re: new RegExp(`^/scm/procurement/supplier-invoices/${U}$`), ctx: { module: 'procurement', docType: 'SupplierInvoice' } },
    { re: new RegExp(`^/scm/inventory/material-requests/${U}$`), ctx: { module: 'procurement', docType: 'MaterialRequest' } },
    { re: new RegExp(`^/qms/initial-inspection-requests/${U}$`), ctx: { module: 'quality', docType: 'InitialInspectionRequest' } },
    { re: new RegExp(`^/prj/projects/${U}$`), ctx: { module: 'projects', docType: 'Project' } },
    { re: new RegExp(`^/finance/payment-vouchers/${U}$`), ctx: { module: 'finance', docType: 'PaymentVoucher' } },
    { re: new RegExp(`^/finance/sales-invoices/${U}$`), ctx: { module: 'finance', docType: 'SalesInvoice' } },
    { re: new RegExp(`^/quality/ncrs/${U}$`), ctx: { module: 'quality', docType: 'NCR' } },
    { re: new RegExp(`^/qms/ncrs/${U}$`), ctx: { module: 'quality', docType: 'NCR' } },
    { re: new RegExp(`^/quality/capas/${U}$`), ctx: { module: 'quality', docType: 'CAPA' } },
    { re: new RegExp(`^/qms/capas/${U}$`), ctx: { module: 'quality', docType: 'CAPA' } },
    { re: new RegExp(`^/assets/${U}$`), ctx: { module: 'assets', docType: 'Asset' } },
    { re: new RegExp(`^/mfg/production-requests/${U}$`), ctx: { module: 'manufacturing', docType: 'ProductionRequest' } },
    { re: new RegExp(`^/contracts/${U}$`), ctx: { module: 'contracts', docType: 'Contract' } },
    { re: new RegExp(`^/contract-reviews/${U}$`), ctx: { module: 'contracts', docType: 'ContractReview' } },
    { re: new RegExp(`^/hr/employees/${U}$`), ctx: { module: 'hr', docType: 'Employee' } },
    { re: new RegExp(`^/crm/customers/${U}$`), ctx: { module: 'sales', docType: 'Customer' } },
    { re: new RegExp(`^/scm/procurement/suppliers/${U}$`), ctx: { module: 'procurement', docType: 'Supplier' } },
  ];

  for (const { re, ctx } of rules) {
    const m = p.match(re);
    if (m) {
      return { ...ctx, docId: m[1] };
    }
  }

  // Module-only hints
  if (p.startsWith('/growth/') || p.startsWith('/crm/')) return { module: 'sales' };
  if (p.startsWith('/mfg/') || p.startsWith('/manufacturing/')) return { module: 'manufacturing' };
  if (p.startsWith('/scm/procurement')) return { module: 'procurement' };
  if (p.startsWith('/scm/inventory') || p.startsWith('/scm/warehouse')) return { module: 'procurement' };
  if (p.startsWith('/finance/') || p.startsWith('/fin/')) return { module: 'finance' };
  if (p.startsWith('/quality/') || p.startsWith('/qms/')) return { module: 'quality' };
  if (p.startsWith('/prj/')) return { module: 'projects' };
  if (p.startsWith('/hr/')) return { module: 'hr' };
  if (p.startsWith('/assets')) return { module: 'assets' };
  if (p.startsWith('/contract')) return { module: 'contracts' };
  if (p.startsWith('/home/tasks')) return { module: 'company' };

  return {};
}

export function personaLabelFromModule(module?: string): string {
  const m = (module || '').toLowerCase();
  if (m === 'sales' || m === 'growth') return 'Growth & Revenue';
  if (m === 'manufacturing' || m === 'mfg') return 'Manufacturing';
  if (m === 'procurement' || m === 'scm') return 'Supply chain';
  if (m === 'finance') return 'Finance';
  if (m === 'quality') return 'Quality';
  if (m === 'company') return 'Tasks';
  if (m === 'projects') return 'Projects';
  if (m === 'assets') return 'Assets';
  if (m === 'hr') return 'HR';
  if (m === 'contracts') return 'Contracts';
  return 'Executive';
}
